import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { Coins, Trophy, Star, Rocket, Wallet } from "lucide-react";
import { useNavigate } from "react-router-dom";

const ForDevelopersSection = () => {
  const navigate = useNavigate();
  return (
    <SectionWrapper id="for-developers">
      <ContentWrapper>
        <LeftContent
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Title>
            For Developers — <Highlight>Earn, Learn & Get Noticed</Highlight>
          </Title>
          <Subtitle>
            Solve real-world coding issues posted by other developers. Earn
            coins, rewards, and grow your reputation. Top solvers get featured
            on our leaderboard and attract job offers.
          </Subtitle>

          <FeatureList>
            <li>
              <Coins size={18} /> Earn Coins or Money for Every Solved Issue
            </li>
            <li>
              <Trophy size={18} /> Compete in Global Leaderboards
            </li>
            <li>
              <Star size={18} /> Build Your Verified Developer Portfolio
            </li>
            <li>
              <Rocket size={18} /> Get Hired for Freelance & Referral Jobs
            </li>
          </FeatureList>

          <CTAButton
            onClick={() => {
              navigate("/code-issues");
            }}
          >
            Start Solving Now
          </CTAButton>
        </LeftContent>

        <RightVisual
          initial={{ opacity: 0, x: 40 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <VisualCard
            whileHover={{ rotate: 3, scale: 1.05 }}
            transition={{ type: "spring", stiffness: 200 }}
          >
            <Coins size={50} />
            <CardTitle>+120 Coins</CardTitle>
            <CardText>Earned solving “React Native Crash Fix”</CardText>
          </VisualCard>

          <VisualCard
            whileHover={{ rotate: -3, scale: 1.05 }}
            transition={{ type: "spring", stiffness: 200 }}
            style={{ top: "180px", left: "80px" }}
          >
            <Trophy size={50} />
            <CardTitle>Top Solver of the Week 🏆</CardTitle>
            <CardText>Rank #3 globally with 45 issues solved</CardText>
          </VisualCard>

          <VisualCard
            whileHover={{ rotate: 2, scale: 1.05 }}
            transition={{ type: "spring", stiffness: 200 }}
            style={{ top: "340px", left: "30px" }}
          >
            <Wallet size={50} />
            <CardTitle>₹1,500 Reward</CardTitle>
            <CardText>Withdrawn to your PayPal wallet</CardText>
          </VisualCard>
        </RightVisual>
      </ContentWrapper>
    </SectionWrapper>
  );
};

export default ForDevelopersSection;

// ---------------- Styled Components ----------------

const SectionWrapper = styled.section`
  width: 100%;
  background: radial-gradient(circle at top left, #0b0617, #000);
  color: #fff;
  padding: 120px 40px;
  display: flex;
  justify-content: center;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  max-width: 1200px;
  gap: 40px;
`;

const LeftContent = styled(motion.div)`
  flex: 1;
  min-width: 300px;
  max-width: 550px;
`;

const Title = styled.h2`
  font-size: 2.4rem;
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 20px;
`;

const Highlight = styled.span`
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: #d2cde9;
  line-height: 1.6;
  margin-bottom: 30px;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin-bottom: 40px;
  display: flex;
  flex-direction: column;
  gap: 14px;

  li {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 1rem;
    color: #e0e0e0;
    svg {
      color: #c59fff;
    }
  }
`;

const CTAButton = styled.button`
  background: linear-gradient(90deg, #9b5cff, #c59fff);
  color: white;
  font-weight: 600;
  border: none;
  padding: 14px 28px;
  border-radius: 12px;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.3s;
  box-shadow: 0 0 15px rgba(155, 92, 255, 0.3);

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 0 25px rgba(155, 92, 255, 0.6);
  }
`;

const RightVisual = styled(motion.div)`
  flex: 1;
  min-width: 300px;
  max-width: 500px;
  position: relative;
  height: 500px;
`;

const VisualCard = styled(motion.div)`
  position: absolute;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(155, 92, 255, 0.3);
  border-radius: 16px;
  padding: 24px;
  width: 260px;
  backdrop-filter: blur(6px);
  text-align: center;
  box-shadow: 0 0 15px rgba(155, 92, 255, 0.2);
  color: #fff;
`;

const CardTitle = styled.h3`
  color: #c59fff;
  margin-top: 12px;
  font-size: 1.1rem;
`;

const CardText = styled.p`
  font-size: 0.9rem;
  color: #ddd;
  margin-top: 6px;
`;
